import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckSquare, Plus, Calendar, User, Link as LinkIcon } from 'lucide-react';
import { UnifiedTodoModal } from '@/components/crm/UnifiedTodoModal';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';
import {
  UnifiedTodo,
  sortTodosByDependency,
  getPriorityColor,
  getStatusBadge,
  isTodoBlocked,
} from '@/lib/todoHelpers';

interface LocationTodosProps {
  locationId: string;
}

export function LocationTodos({ locationId }: LocationTodosProps) {
  const [selectedTodo, setSelectedTodo] = useState<UnifiedTodo | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: todos = [], isLoading, isError } = useQuery({
    queryKey: ['charging-location-todos', locationId],
    queryFn: async () => {
      console.log('[LocationTodos] Fetching todos for location:', locationId);
      
      const { data, error } = await supabase
        .from('unified_todos' as any)
        .select(`
          *,
          profiles:assigned_to(id, display_name, email),
          parent_todo:depends_on_todo_id(id, title, status)
        `)
        .eq('location_id', locationId)
        .is('deleted_at', null)
        .order('due_date', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[LocationTodos] Query error:', {
          error,
          message: error.message,
          code: error.code,
          details: error.details,
          locationId: locationId
        });
        throw error;
      }
      
      console.log('[LocationTodos] Fetched', data?.length || 0, 'todos for location');
      return (data || []) as any as UnifiedTodo[];
    },
    retry: 1,
  });

  const handleToggleComplete = async (todo: UnifiedTodo) => {
    const newStatus = todo.status === 'completed' ? 'open' : 'completed';
    await supabase
      .from('unified_todos' as any)
      .update({
        status: newStatus,
        completed_at: newStatus === 'completed' ? new Date().toISOString() : null,
      })
      .eq('id', todo.id);
    
    queryClient.invalidateQueries({ queryKey: ['charging-location-todos', locationId] });
  };

  // Sort todos by dependency chain
  const sortedTodos = sortTodosByDependency(todos);

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2">
            <CheckSquare className="w-5 h-5" />
            Taken ({todos.filter((t: UnifiedTodo) => t.status !== 'completed').length})
          </CardTitle>
          <Button
            size="sm"
            onClick={() => {
              setSelectedTodo(null);
              setIsModalOpen(true);
            }}
          >
            <Plus className="w-4 h-4 mr-1" />
            Taak
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {isLoading ? (
            <p className="text-center text-muted-foreground py-4">Laden...</p>
          ) : isError ? (
            <p className="text-center text-destructive py-4">Fout bij laden van taken</p>
          ) : todos.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Geen taken toegevoegd</p>
          ) : (
            sortedTodos.map((todo: any) => {
              const isBlocked = isTodoBlocked(todo, todos);
              const statusBadge = getStatusBadge(isBlocked ? 'blocked' : todo.status);
              const isOverdue =
                todo.due_date && new Date(todo.due_date) < new Date() && todo.status !== 'completed';

              return (
                <div
                  key={todo.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors cursor-pointer ${
                    todo.status === 'completed' ? 'opacity-60' : ''
                  }`}
                  onClick={() => {
                    setSelectedTodo(todo);
                    setIsModalOpen(true);
                  }}
                >
                  <Checkbox
                    checked={todo.status === 'completed'}
                    onCheckedChange={() => handleToggleComplete(todo)}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-1"
                    disabled={isBlocked}
                  />

                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className={`font-medium text-sm ${todo.status === 'completed' ? 'line-through' : ''}`}>
                        {todo.title}
                      </h4>
                      <Badge variant={statusBadge.variant} className="text-xs">
                        {statusBadge.label}
                      </Badge>
                      <Badge className={`text-xs ${getPriorityColor(todo.priority)}`}>
                        {todo.priority}
                      </Badge>
                      {isOverdue && <Badge variant="destructive" className="text-xs">Verlopen</Badge>}
                      {isBlocked && todo.parent_todo && (
                        <Badge variant="outline" className="text-xs">
                          <LinkIcon className="h-3 w-3 mr-1" />
                          Wacht op: {todo.parent_todo.title}
                        </Badge>
                      )}
                    </div>

                    {todo.description && (
                      <p className="text-xs text-muted-foreground mb-1 whitespace-pre-wrap">{todo.description}</p>
                    )}

                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      {todo.due_date && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {format(new Date(todo.due_date), 'dd MMM', { locale: nl })}
                        </div>
                      )}
                      {todo.profiles && (
                        <div className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {todo.profiles.display_name || todo.profiles.email}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>

      <UnifiedTodoModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTodo(null);
        }}
        defaultLocationId={locationId}
        todo={selectedTodo}
        onSave={async () => {
          await queryClient.invalidateQueries({ queryKey: ['charging-location-todos', locationId] });
        }}
      />
    </Card>
  );
}
